import React, { useRef, useEffect } from "react";
import Color from "color";
import { useGlobalValue } from "../../../state/GlobalProvider";
import { format, getPropertyValue, getValueFromJson } from "../../../utils";
import styles from "./BarChart.module.sass";
const TICKS_FONT_SIZE = 12;

const composeSummarizedData = data => {
  let minPeriod = 1;
  let maxPeriod = 12;
  const summarized = data.reduce(([legend, summaryData], item, period) => {
    minPeriod = Math.min(minPeriod, period);
    maxPeriod = Math.max(maxPeriod, period);
    const summary = item.summary || [];
    summary.forEach(({
      key,
      value
    }) => {
      let index = summaryData.findIndex(([{
        key: k
      }]) => k === key);
      index = index >= 0 ? index : summaryData.length;

      if (!summaryData[index]) {
        summaryData[index] = [];
      }

      summaryData[index].push({
        key,
        period,
        value
      });

      if (!legend.includes(key)) {
        legend.push(key);
      }
    });
    return [legend, summaryData];
  }, [[], []]);
  return [summarized[0], summarized[1].map(dataset => {
    let result = [];

    for (let i = minPeriod, l = maxPeriod; i <= l; i++) {
      const entry = dataset.find(({
        period
      }) => period === i);

      if (entry) {
        result.push({
          category: entry.key,
          period: i,
          value: entry.value
        });
      } else if (data[i] && data[i].summary !== null) {
        result.push({
          category: undefined,
          period: i,
          value: 0
        });
      }
    }

    return result; // return dataset.map(({ key, period, value }) => ({ category: key, period, value }))
  })];
};

const transform = (data, summarized = false, dataLabels, colors) => {
  const labels = [];

  if (summarized) {
    const [legend, summaryData] = composeSummarizedData(data);
    data = summaryData;
    dataLabels = dataLabels || legend;
  }

  const chartColors = [getPropertyValue("--chart-primary-color").trim(), getPropertyValue("--chart-secondary-color").trim()];
  const secondaryColor = new Color(chartColors[1]);
  const sFade = secondaryColor.contrast(Color("white")) > 3 ? "lighten" : "darken";

  for (let index = 0; index < data.length - 2; index++) {
    chartColors.push(secondaryColor[sFade](0.2 * (index + 1)).hex());
  }

  const datasets = data.map((dataset, dsIndex) => {
    if (!dataset) {
      return [];
    }

    const datasetValues = [];
    let dataCategory;

    if (Array.isArray(dataset)) {
      dataset.forEach(({
        value,
        category,
        ...rest
      }) => {
        const label = Object.entries(rest)[0][0];
        const labelValue = `${rest[label]}`;

        if (!labels.includes(labelValue)) {
          labels.push(labelValue);
        }

        if (!dataCategory) {
          dataCategory = category;
        }

        datasetValues.push(value);
      });
    }

    return {
      label: (dataLabels || [])[dsIndex],
      data: datasetValues,
      dataCategory,
      backgroundColor: chartColors[dsIndex]
    };
  });
  return {
    labels,
    datasets
  };
};

const renderChart = (canvas, data, summarized, dataLabels, labelsPosition, currency, stacked, links, colors, goto, params, i18n, currencyType) => {
  let chartData = null;
  const style = getComputedStyle(canvas);
  const ctx = canvas.getContext("2d");
  const chartTextColor = labelsPosition !== 'top' ? getPropertyValue("--chart-text-color").trim() : getPropertyValue("--chart-text-top-color").trim();
  chartData = data ? transform(data, summarized, dataLabels, [colors.$chartSecondaryColor, colors.$chartBackgroundColor]) : null;
  const chart = new Chart(ctx, {
    type: "bar",
    data: chartData,
    options: {
      showTooltips: true,
      layout: {
        padding: {
          left: -14,
          top: labelsPosition === "top" ? 10 + 12 * Math.max(1, (chartData.datasets || []).length) : 12
        }
      },
      hover: {
        mode: "nearest",
        animationDuration: 0,
        onHover: (e, chartElements) => {
          e.target.style.cursor = chartElements[0] ? "pointer" : "default";
        }
      },
      onClick: evt => {
        const activePoints = chart.getElementAtEvent(evt);
        const tooltipItem = activePoints[0] || {};
        const link = (links || [])[tooltipItem._datasetIndex] || (links || [])[0];
        const filterValue = tooltipItem._model && tooltipItem._model.label;

        if (goto && link && Object.keys(link).length && filterValue) {
          const dataset = chart.config.data.datasets[tooltipItem._datasetIndex] || {
            data: []
          };
          const category = dataset.dataCategory;
          setTimeout(() => {
            const option = ((link || {}).options || {})[params.entity];
            goto(link.to, [...(link.filter || []), {
              column: link.filterColumn,
              filters: [{
                value: filterValue
              }],
              operator: link.operator
            }, category && link.category ? {
              column: link.category,
              filters: [{
                value: category
              }]
            } : category && option ? {
              column: option,
              filters: [{
                value: category
              }]
            } : undefined].filter(f => f));
          });
        }
      },
      tooltips: {
        enabled: true,
        callbacks: {
          title: function (tooltipItem, data) {
            const dataset = data.datasets[tooltipItem[0].datasetIndex] || {
              labels: []
            };

            if (!summarized && dataset.label) {
              return `${(data.labels || [])[tooltipItem[0].index] || ""}: ${i18n`${dataset.label}`}`;
            }

            return `${(data.labels || [])[tooltipItem[0].index] ? `${dataset.dataCategory ? `${(data.labels || [])[tooltipItem[0].index] || ""}: ` : (data.labels || [])[tooltipItem[0].index]}` : ""} ${dataset.dataCategory ? i18n`${dataset.dataCategory}` : ""}`;
          },
          label: function (tooltipItem) {
            const formatted = format(tooltipItem.yLabel, currency ? "short" : undefined, currency ? getValueFromJson(currencyType, 'currency') : null, currency ? getValueFromJson(currencyType, 'fractionDigits') || 2 : 0, getValueFromJson(currencyType, 'localeCurrency'));
            return formatted;
          }
        },
        displayColors: false,
        backgroundColor: colors.$chartTooltipBackgroundColor,
        borderColor: colors.$chartTooltipBorderColor,
        borderWidth: 1,
        cornerRadius: 0
      },
      animation: {
        duration: 0
      },
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: true,
        position: "bottom",
        onClick: e => e.stopPropagation(),
        labels: {
          fontFamily: style.fontFamily,
          fontSize: 10,
          fontColor: colors.$chartSecondaryColor,
          boxHeight: 4,
          boxWidth: 6,
          filter: legendItem => {
            if (legendItem.text) {
              legendItem.text = i18n`${legendItem.text}`;
              return legendItem;
            }
          }
        }
      },
      scales: {
        xAxes: [{
          stacked: stacked,
          gridLines: {
            display: false,
            drawBorder: false
          },
          categoryPercentage: 0.8,
          barPercentage: 0.5 + Math.min(0.3, 0.1 * (chartData.datasets || []).length),
          maxBarThickness: Math.min(Math.round(canvas.parentNode.offsetWidth), 60),
          ticks: {
            beginAtZero: true,
            fontStyle: "bold",
            fontColor: style.color
          }
        }],
        yAxes: [{
          stacked: stacked,
          gridLines: {
            display: true,
            drawBorder: false,
            color: style.borderColor,
            zeroLineColor: style.borderColor
          },
          ticks: {
            beginAtZero: true,
            padding: 10,
            fontStyle: "normal",
            fontColor: style.fill,
            fontSize: TICKS_FONT_SIZE,
            callback: function (label, index, labels) {
              return index === 0 || label === 0 || index === labels.length - 1 ? "" : null;
            }
          }
        }]
      },
      plugins: {
        chartJsPluginBarChartBackground: {
          color: getPropertyValue("--chart-background-color"),
          backgroundOffset: 6
        }
      }
    },
    plugins: [{
      afterDatasetsDraw: function (chartInstance) {
        const innerCtx = chartInstance.ctx;
        innerCtx.font = Chart.helpers.fontString(parseFloat(style.fontSize), style.fontWeight, style.fontFamily);
        innerCtx.save();
        innerCtx.textAlign = "center";
        innerCtx.textBaseline = "bottom";
        const stacked = (chartInstance.config.options.scales.xAxes.find(({
          stacked
        }) => stacked) || {}).stacked;
        const topOffset = stacked ? 10 + 10 * chartInstance.config.data.datasets.length : 20;
        chartInstance.config.data.datasets.forEach((dataset, i) => {
          innerCtx.fillStyle = chartTextColor;
          const meta = chartInstance.controller.getDatasetMeta(i);
          const yOffset = topOffset + 10 * i * (stacked ? -1 : 1);
          meta.data.forEach(function (bar, index) {
            const x = bar._view.x;
            const data = dataset.data[index];

            if (data) {
              const formatted = format(data, currency ? "short" : undefined, currency ? getValueFromJson(currencyType, 'currency') : null, currency ? getValueFromJson(currencyType, 'fractionDigits') || 2 : 0, getValueFromJson(currencyType, 'localeCurrency'));

              if (labelsPosition === "top") {
                innerCtx.fillText(formatted, x, yOffset);
              } else {
                innerCtx.fillText(formatted, bar._model.x, bar._model.y + (bar._model.base - bar._model.y) / 2 + 5);
              }
            }
          });
        });
        innerCtx.restore();
      }
    }]
  });
  return chart;
};

const BarChart = props => {
  const {
    theme,
    goto,
    params,
    i18n
  } = useGlobalValue();
  const {
    data,
    summarized = false,
    dataLabels,
    labelsPosition = "top",
    links,
    stacked,
    currency = false,
    modifiers,
    currencyType
  } = props;
  const canvas = useRef(null);
  const {
    colors = {}
  } = theme || {};
  useEffect(() => {
    const chart = canvas.current && data && renderChart(canvas.current, data || [], summarized, dataLabels, labelsPosition, currency, stacked, links || [], colors, goto, params, i18n, currencyType);
    return () => {
      chart && chart.destroy();
    };
  }, [canvas, data, colors]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles["root"]
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("canvas", {
    ref: canvas,
    className: (modifiers || []).includes("--top-text-small") ? styles["text-small"] : styles[""]
  })));
};

export default BarChart;